import React from "react";
import ConfirmedMark from "./ConfirmedMark";

/**
 * The end of the road, for either public form.
 *
 * Terminal on purpose: there is no button back, no reference number and
 * nothing to return to. A second submission is a second record, and the
 * practitioner has no way to tell a duplicate from a genuine second person —
 * so the screen that replaces the form does not offer to send another.
 *
 * "You can close the browser" is doing real work, and is pinned to the foot of
 * the screen rather than tucked under the paragraph. Somebody who has just
 * filled in a form from a link does not otherwise know whether they are
 * supposed to wait for something — and a line that answers that belongs where
 * the eye lands last, not in the middle of the page competing with the message
 * it is a footnote to.
 *
 * Shared rather than written twice. The enquiry form and the pre-consultation
 * form arrive on the same phone from the same practitioner, and everything
 * about this screen except one sentence is the same on both — so one sentence
 * is all either of them passes in.
 */

interface FormSubmittedProps {
  /** The line under the heading, which is the only part that differs. */
  message: React.ReactNode;
}

const FormSubmitted: React.FC<FormSubmittedProps> = ({ message }) => (
  // Fills the shell's main, which is a flex column — that is what lets the
  // message centre in the space above while the closing line stays at the foot
  // however tall the screen is.
  <div className="flex flex-1 flex-col">
    <div className="flex flex-1 flex-col justify-center sm:items-center sm:text-center">
      <ConfirmedMark />

      <h1 className="mt-6 text-[42px] leading-[1.15] sm:text-[44px] font-semibold tracking-[-0.02em] text-[#0A4A52]">
        Submitted successfully!
      </h1>

      <p className="mt-4 max-w-[520px] text-[18px] sm:text-[20px] font-medium leading-7 text-brand-dark">
        {message}
      </p>
    </div>

    <p className="pt-10 text-center text-[14px] text-ink-subtle">
      You can close the browser
    </p>
  </div>
);

export default FormSubmitted;
